import { AccountKindService } from '@frontendlib/common/services/account-kind.service';
import { SpaceType } from '@sharedlib/common/types/SpaceType';
import { ItemChangesComponent } from '@modules/team-item-changes/item-changes.component';
import { ItemReviewComponent } from '@modules/common/components/item-review/item-review.component';
import {
  ITEM_REVIEW_INLINE_HOST,
  type ItemReviewTarget,
} from '@modules/common/components/item-review/item-review-dialog.service';
import { ItemHandoverComponent } from '@modules/common/components/item-handover/item-handover.component';
import {
  ITEM_HANDOVER_INLINE_HOST,
  type ItemHandoverTarget,
} from '@modules/common/components/item-handover/item-handover-dialog.service';
import { UILocations } from '@modules/common/types/uiLocations';
import { itemDisplayIdentity } from '@modules/common/components/item-workspace/item-display-identity';
import { itemChangeRevision } from '@modules/common/components/item-workspace/item-change-revision';
import { ItemQuickCommentComponent, type ItemFooterTab } from '@modules/comments/components/item-quick-comment/item-quick-comment.component';
import { ItemWorkspaceChipComponent } from '@modules/common/components/item-workspace/item-workspace-chip.component';
import { ItemWorkspaceAccessComponent } from '@modules/common/components/item-workspace/item-workspace-access.component';
import { ItemTeamLabelComponent } from '@modules/common/components/item-workspace/item-team-label.component';
import { ItemWorkspaceFooterComponent } from '@modules/common/components/item-workspace/item-workspace-footer.component';
import { APP_CRUMBS, DetailNavigation } from '@modules/common/components/detail-breadcrumbs/detail-navigation';
import { type DetailCrumb } from '@modules/common/components/detail-breadcrumbs/detail-breadcrumbs.component';
import { TitleByFilesFilterPipe } from '@modules/files/pipes/title-by-files-filter.pipe';
import { itemWorkspace } from '@modules/common/components/item-workspace/item-workspace-scope';
import { HttpContext } from '@angular/common/http';
import {
  ChangeDetectorRef,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  Injector,
  Input,
  linkedSignal,
  Signal,
  signal,
  type SimpleChanges,
  untracked,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DeleteConfirmationModalService } from '@modules/common/services/delete-confirmation-modal.service';
import { AlertService } from '@modules/alert/services/alert.service';
import { type AlertButtonConfig } from '@modules/alert/types/config';
import { headerLocationBadge } from '@modules/common/utils/header-location-badge';
import { Application } from '@modules/common/types/application';
import { MAILBOX_TENANT_ID, OWNING_SPACE_ID, owningTenantContext } from '@modules/common/interceptors/spaces.interceptor';
import { DirtyStateHelper } from '@modules/common/utils/dirty-form.helper';
import { type StitchRequestConfig } from '@modules/common/services/base-stitch.service';
import { type WorkspaceIdentity, WorkspaceIdentityService } from '@modules/common/services/workspace-identity.service';
import { AdvancedSearchService } from '@modules/search/services/advanced-search.service';
import { FolderSummariesCache } from '@modules/files/services/folder-summaries.cache';
import { FormControl, FormGroup } from '@angular/forms';
import { type NativeFile } from '@modules/common/types/native-file';
import { type Upload } from '@modules/common/types/upload';
import {
  FILE_INTENT_INLINE_HOST,
  type FileFormIntent,
  FileFormIntentOpener,
} from '@modules/files/version-history/file-form-intent';
import { fileVersionError, FileVersionsClient } from '@modules/files/version-history/file-versions.client';
import { FileVersionHistoryComponent } from '@modules/files/version-history/file-version-history.component';
import { canEditFilesIn } from '@modules/files/version-history/file-edit-authority';
import type { FilesDetailsDto } from '@sharedlib/dtos/dto/files-app/FilesDetailsDto';
import type { FileSummaryDto } from '@sharedlib/dtos/dto/files-app/FileSummaryDto';
import { environment } from '@environment';
import type { AutocompleteFactory } from '@frontendlib/common/types/autocomplete-factory';
import { FullFormBaseComponent } from '@modules/common/components/full-form-base/full-form-base.component';
import { Tab } from '@modules/common/types/tab';
import { TabsComponent } from '@modules/common/components/tabs/tabs.component';
import { type DragData, DragDataTypes, getStringField } from '@modules/drag-n-drop/types/drag-data';
import { FilesService } from '@modules/files/services/files.service';
import { FoldersService } from '@modules/files/services/folders.service';
import { FilesTenantScopeService } from '@modules/files/services/files-tenant-scope.service';
import { FilesAppStateService } from '@modules/files/services/state.service';
import { File, type FileFormGroupConfig } from '@modules/files/types/file';
import { hasFileLink, isValidFileLink } from '@modules/files/types/file-link';
import { Folder } from '@modules/files/types/folder';
import { ContainerDndService } from '@modules/common/services/container-dnd.service';
import { CROSS_WORKSPACE_DROP_TEXT } from '@modules/files/types/cross-workspace-drop-text';
import { FilesInputComponent } from '@modules/form-controls/components/files-input/files-input.component';
import { formatFileSize } from '@sharedlib/common/utils/file-size';
import { SignalStitchEventType } from '@sharedlib/common/types/signal-type';
import { StitchType } from '@sharedlib/common/types/StitchType';
import { EMPTY, merge, type Observable, Subject } from 'rxjs';
import { catchError, filter, finalize, map, switchMap, take, takeUntil, tap } from 'rxjs/operators';

@Component({
  selector: 'app-file-form',
  templateUrl: './file-form.component.html',
  styleUrls: ['./file-form.component.less'],
  standalone: false,
  providers: [
    WorkspaceIdentityService,
    // The Replace file version requests, through this form's space-context client (#2074).
    FileVersionsClient,
    {
      provide: ITEM_REVIEW_INLINE_HOST,
      useFactory: () => {
        const form = inject(FileFormComponent);
        return { open: (target: ItemReviewTarget): boolean => form.openInlineReview(target) };
      },
    },
    {
      provide: ITEM_HANDOVER_INLINE_HOST,
      useFactory: () => {
        const form = inject(FileFormComponent);
        return { open: (target: ItemHandoverTarget): boolean => form.openInlineAssignment(target) };
      },
    },
    {
      provide: FILE_INTENT_INLINE_HOST,
      useFactory: () => {
        const form = inject(FileFormComponent);
        return {
          open: (file: Pick<FileSummaryDto, 'id' | 'accountId'>, intent: FileFormIntent): boolean =>
            form.openFileIntent(file, intent),
        };
      },
    },
  ],
})
export class FileFormComponent extends FullFormBaseComponent<StitchType.FILE, File, FileFormGroupConfig> {
  public readonly tabsOverflow = input<'dropdown' | 'scroll'>('dropdown');
  protected readonly StitchType = StitchType;
  public readonly reviewApi = inject(FilesService).reviewApi;
  private readonly reviewControl = viewChild(ItemReviewComponent);
  public openInlineReview(target: ItemReviewTarget): boolean {
    const control = this.reviewControl();
    if (
      !this.handoverInsideModal() ||
      !control ||
      target.itemId !== this.file?.id ||
      target.tenantId !== this.handoverTenantId()
    )
      return false;
    control.openRequest();
    return true;
  }
  public readonly handoverApi = inject(FilesService).handoverApi;
  private readonly handoverControl = viewChild(ItemHandoverComponent);
  /** A hand-over is a team feature: a personal login gets none of its controls, whichever space is open. */
  private readonly accountKind = inject(AccountKindService);
  private readonly hostElement = inject<ElementRef<HTMLElement>>(ElementRef);
  protected readonly handoverInsideModal = computed(
    () => !!this.hostElement.nativeElement.closest('app-full-form-modal'),
  );
  protected readonly handoverTenantId = computed(() => {
    this.workspaceRevision();
    // A save writes the new file's id and team onto this same object; nothing else tells this computed.
    this.reviewContentVersion();
    return this.accountKind.isTeam() && itemWorkspace(this.workspaceIdentity(), this.file) === 'team'
      ? this.file.accountId
      : undefined;
  });
  /** The role's File update rule on the file's team, as files-app asks before a hand-over and as Replace already uses. */
  protected readonly canAssignHandover = computed(() => canEditFilesIn(this.permissionsService, this.handoverTenantId()));
  public openInlineAssignment(target: ItemHandoverTarget): boolean {
    const control = this.handoverControl();
    if (
      !this.canAssignHandover() ||
      !this.handoverInsideModal() ||
      !control ||
      target.itemId !== this.file?.id ||
      target.tenantId !== this.handoverTenantId()
    )
      return false;
    control.openAssign();
    return true;
  }

  protected readonly UILocations = UILocations;
  protected readonly workspaceRevision = itemChangeRevision(this.stitchItemChanged);

  private readonly fileIntents = inject(FileFormIntentOpener);
  private readonly versionsClient = inject(FileVersionsClient);
  /** The File tab's replace control keeps its own upload control, so the form itself stays pristine. */
  protected readonly replaceOpen = signal(false);
  protected readonly replaceError = signal<string | null>(null);
  protected readonly replaceUpload = new FormControl<Upload | null>(null);
  protected readonly replaceTitle = new FormControl<string>('');
  protected replaceOwner: ReadonlyArray<{ readonly accountId?: string }> = [];
  private static replaceLabels = 0;
  protected readonly replaceLabelId = `file-replace-label-${++FileFormComponent.replaceLabels}`;
  private replaceGeneration = 0;
  /** A fresh object after a replacement, so the preview loads the new bytes. */
  protected previewFile: File | undefined;

  /** A team's own saved upload, not in Trash, for a role that may edit documents there: the server refuses Replace elsewhere today. */
  protected canReplace(): boolean {
    return (
      !!this.file?.id &&
      !hasFileLink(this.file) &&
      !this.file.deleted &&
      !!this.handoverTenantId() &&
      canEditFilesIn(this.permissionsService, this.file.accountId)
    );
  }
  public openFileIntent(file: Pick<FileSummaryDto, 'id' | 'accountId'>, intent: FileFormIntent): boolean {
    return this.fileIntents.matches(file, this.file) && this.applyFileIntent(intent);
  }
  // A row's intent waits for the form that shows that file; it applies once.
  private readonly fileIntent = effect(() => {
    this.workspaceRevision();
    this.handoverTenantId();
    const request = this.fileIntents.requested();
    untracked(() => {
      if (!request || !this.fileIntents.matches(request.file, this.file)) return;
      if (this.applyFileIntent(request.intent)) this.fileIntents.requested.set(null);
    });
  });
  private applyFileIntent(intent: FileFormIntent): boolean {
    if (intent === 'versions') {
      if (!this.historyAvailable()) return false;
      this.selectedTab = 'versions';
      return true;
    }
    if (!this.canReplace()) return false;
    this.selectedTab = 'file';
    this.openReplace();
    return true;
  }
  /** A team's own saved upload inside that team, as the note menu offers a note's history. */
  protected historyAvailable(): boolean {
    return (
      !!this.file?.id &&
      !hasFileLink(this.file) &&
      this.spaceContextStore.type() === SpaceType.SHARED_SPACE &&
      this.file.accountId === this.spaceContextStore.contextId()
    );
  }
  protected get versionHistoryInputs(): Record<string, unknown> {
    return {
      file: this.file,
      // The role's Edit Documents rule in the file's own workspace, as Replace asks it.
      canEdit: canEditFilesIn(this.permissionsService, this.file.accountId),
      restored: this.applyRestoredFile,
    };
  }
  private readonly applyRestoredFile = (item: FilesDetailsDto): void => this.applyFileContent(item);
  protected openReplace(): void {
    this.resetReplace();
    this.replaceOwner = [{ accountId: this.file.accountId }];
    this.replaceOpen.set(true);
    this.lifecycleCdr.markForCheck();
  }
  protected cancelReplace(): void {
    this.resetReplace();
  }
  private resetReplace(): void {
    this.replaceGeneration++;
    this.replaceOpen.set(false);
    this.replaceError.set(null);
    this.replaceUpload.setValue(null);
    this.replaceTitle.setValue('');
  }
  protected onReplaceUploading(active: boolean): void {
    const upload = this.replaceUpload.value;
    if (active || !this.canReplace() || !upload?.id || !upload.checksum || upload.error) return;
    const target = { id: this.file.id, accountId: this.file.accountId };
    const generation = ++this.replaceGeneration;
    this.versionsClient
      .replace(target, { tempAttachmentId: upload.id, attachmentName: upload.name ?? upload.nativeFile?.name ?? '' })
      .pipe(takeUntil(this.alive))
      .subscribe({
        next: ({ item }) => {
          if (generation === this.replaceGeneration) this.applyFileContent(item);
        },
        error: (error: unknown) => {
          if (generation !== this.replaceGeneration) return;
          this.replaceUpload.setValue(null);
          this.replaceError.set(fileVersionError(error, 'The file could not be replaced. Try again.'));
          this.lifecycleCdr.markForCheck();
        },
      });
  }
  /** A replace or restore answer is the file's current content; typed, unsaved details stay as typed. */
  private applyFileContent(item: FilesDetailsDto): void {
    const fresh = new File(item);
    if (fresh.id !== this.file.id || fresh.accountId !== this.file.accountId) return;
    const pristine = this.pristine;
    Object.assign(this.file, fresh);
    this.previewFile = new File({ ...this.file });
    this.resetReplace();
    if (pristine) this.stitchItemChanged.next();
    this.itemUpdated.emit(this.file);
    this.lifecycleCdr.markForCheck();
  }
  protected readonly reviewContentVersion = itemChangeRevision(this.itemUpdated.pipe(map(() => undefined)));

  protected readonly workspaceComponents = {
    'app-item-changes': ItemChangesComponent,
    'app-item-team-label': ItemTeamLabelComponent,
    'app-item-workspace-chip': ItemWorkspaceChipComponent,
    'app-item-workspace-access': ItemWorkspaceAccessComponent,
    'app-item-quick-comment': ItemQuickCommentComponent,
    'app-item-workspace-footer': ItemWorkspaceFooterComponent,
    'app-file-version-history': FileVersionHistoryComponent,
  };

  /** Which file folder the open item is filed under; see {@link headerLocationBadge}. */
  protected readonly locationBadge = headerLocationBadge({
    changed: this.stitchItemChanged,
    containerId: () => this.stitchItem?.folderId,
    cache: inject(FolderSummariesCache),
    search: inject(AdvancedSearchService),
    application: Application.files,
    badgeType: 'file-folder',
    alsoWhen: this.openedFromHub,
  });

  private readonly titleByFilesFilter = new TitleByFilesFilterPipe(inject(Injector));
  /** stitch-app-shell breadcrumbs, as in every app. */
  protected readonly navigation = new DetailNavigation(FilesAppStateService, {
    appLabel: APP_CRUMBS.files,
    viewTitle: (folder) => this.titleByFilesFilter.transform(folder, null, true),
  });

  /** [workspace ›] Files app › the list's view › this file. */
  protected breadcrumbs(): DetailCrumb[] {
    this.workspaceRevision();
    return this.navigation.crumbs({
      workspace: itemWorkspace(this.displayWorkspaceIdentity(), this.stitchItem),
      container: this.headerFolder()?.title,
      fallbackView: 'Files',
      itemTitle: this.file.title || 'Untitled file',
      itemId: this.file?.id,
    });
  }

  /** The folder this file sits in, named by the header's container badge. */
  protected readonly headerFolder = inject(FolderSummariesCache).get(
    computed(() => {
      this.workspaceRevision();
      return this.stitchItem?.folderId ?? null;
    }),
  );

  private readonly filesInput: Signal<FilesInputComponent | undefined> = viewChild(FilesInputComponent);

  public readonly pickedFile = input<NativeFile | undefined>();
  private pickedFileConsumed = false;
  private readonly consumePickedFile = effect(() => {
    const filesInput = this.filesInput();
    const picked = this.pickedFile();
    if (!filesInput || !picked || this.pickedFileConsumed) return;
    this.pickedFileConsumed = true;
    untracked(() => filesInput.addFiles([picked]));
  });

  /** Destructive actions wait for complete authoritative lifecycle flags. */
  protected readonly lifecycleState = signal<'ready' | 'reading' | 'unknown'>('ready');
  private readonly lifecycleRebound = new Subject<void>();
  private readonly lifecycleCdr = inject(ChangeDetectorRef);
  private lifecycleGeneration = 0;
  private readonly lifecycleInvalidated = new Subject<void>();
  private readonly deleteConfirmation = inject(DeleteConfirmationModalService);
  private purgePending = false;

  public override delete(): void {
    if (!this.file?.id || this.lifecycleState() !== 'ready') return;
    if (!this.file.deleted) {
      super.delete();
      return;
    }
    if (this.purgePending) return;
    const target = { id: this.file.id, accountId: this.file.accountId };
    const generation = this.lifecycleGeneration;
    this.purgePending = true;
    this.deleteConfirmation
      .confirm(StitchType.FILE)
      .pipe(
        take(1),
        filter(
          () =>
            generation === this.lifecycleGeneration &&
            this.lifecycleState() === 'ready' &&
            this.file.id === target.id &&
            this.file.accountId === target.accountId &&
            this.file.deleted,
        ),
        switchMap(() =>
          this.filesService.deletePermanently(
            { ids: [target.id] },
            { skipConfirmation: true, emit: false, tenantItems: [target] },
          ),
        ),
        takeUntil(merge(this.lifecycleInvalidated, this.lifecycleRebound, this.alive)),
        finalize(() => (this.purgePending = false)),
        catchError(() => EMPTY),
      )
      .subscribe(() => this.handleClose());
  }

  protected trashButtonClick(event: MouseEvent): void {
    // The existing icon emits its own action; keyboard/button presses arrive here.
    if (event.target === event.currentTarget) this.delete();
  }

  public changesKey = 'file';
  public tabs: Tab[] = [
    { title: 'File', value: 'file' },
    { title: 'Details', value: 'details' },
    { title: 'Hub', value: 'hub' },
    { title: 'Stitch', value: 'stitch' },
    { title: 'Comments', value: 'comments' },
  ];
  public foldersSuggestions: AutocompleteFactory<Folder>;

  protected dragDataType = DragDataTypes.file;

  protected readonly footerBadge = headerLocationBadge({
    changed: this.stitchItemChanged,
    containerId: () => this.stitchItem?.folderId,
    cache: inject(FolderSummariesCache),
    badgeType: 'file-folder',
    shown: computed(() => !!this.displayWorkspaceIdentity()),
  });
  @Input() file = new File();

  private readonly filesTenantScopeService = inject(FilesTenantScopeService, { optional: true });
  private readonly workspaceIdentityService = inject(WorkspaceIdentityService);
  private readonly alertService = inject(AlertService);

  /** The accepted team identity, or undefined when this form is mounted in a private profile. */
  protected readonly workspaceIdentity = signal<WorkspaceIdentity | undefined>(undefined);
  protected readonly displayWorkspaceIdentity = itemDisplayIdentity(
    this.workspaceIdentity,
    this.workspaceIdentityService,
    this.stitchItemChanged,
    () => this.stitchItem,
    () => this.dp,
  );

  /** The tenant selected for a new file; saved files always keep their own accountId. */
  protected readonly sharingTenantId = signal<string | null>(null);

  /** Makes the primary action state the destination while the picker is present. */
  protected readonly saveLabel = computed(() => {
    const identity = this.workspaceIdentity();
    if (!identity) {
      return 'Save';
    }
    return this.sharingTenantId() === identity.spaceTenantId ? 'Save to team workspace' : 'Save to my workspace';
  });

  private fileTabActivated = false;
  protected readonly hasFileLink = hasFileLink;
  protected readonly isValidFileLink = isValidFileLink;

  protected get shouldRenderPreview(): boolean {
    if (hasFileLink(this.file)) return false;
    this.fileTabActivated ||= this.selectedTab === 'file';

    return this.fileTabActivated && !!this.file?.id;
  }

  protected readonly validationMessages = computed(() => ({
    required: 'Please select a file to upload',
    size: `File is too large. Maximum size ${formatFileSize(this.maxUploadSize() ?? environment.fileSizeLimit)}.`,
    uploading: "Can't upload file",
  }));

  constructor(
    private filesService: FilesService,
    foldersService: FoldersService,
    private stateService: FilesAppStateService,
    injector: Injector,
  ) {
    super(injector, filesService);
    this.foldersSuggestions = foldersService.getAutocompleteSuggestions();
    this.stitchEventsService
      .getEvents(() => (this.file.accountId ? [this.file.accountId] : []), StitchType.FILE, [
        SignalStitchEventType.RESTORED_FROM_TRASH,
        SignalStitchEventType.MOVED_TO_TRASH,
        SignalStitchEventType.ARCHIVED,
        SignalStitchEventType.UNARCHIVED,
      ])
      .pipe(
        filter((event) => !!this.file.id && event.spaceId === this.file.accountId && event.ids.includes(this.file.id)),
        switchMap(() => {
          const target = { id: this.file.id, accountId: this.file.accountId };
          const generation = this.lifecycleGeneration;
          this.lifecycleInvalidated.next();
          this.lifecycleState.set('reading');
          this.lifecycleCdr.markForCheck();
          return this.filesService.getLifecycle(target).pipe(
            take(1),
            takeUntil(this.lifecycleRebound),
            filter(
              () =>
                generation === this.lifecycleGeneration &&
                this.file.id === target.id &&
                this.file.accountId === target.accountId,
            ),
            map((response) => {
              const item = response && typeof response === 'object' && 'item' in response ? response.item : undefined;
              if (
                !item ||
                typeof item !== 'object' ||
                !('id' in item) ||
                item.id !== target.id ||
                !('accountId' in item) ||
                item.accountId !== target.accountId ||
                !('deleted' in item) ||
                typeof item.deleted !== 'boolean' ||
                !('archived' in item) ||
                typeof item.archived !== 'boolean'
              ) {
                this.lifecycleState.set('unknown');
                this.lifecycleCdr.markForCheck();
                return;
              }
              const flags = { deleted: item.deleted, archived: item.archived };
              Object.assign(this.file, flags);
              this.form.patchValue(flags, { emitEvent: false });
              this.originalFormValue = { ...this.originalFormValue, ...flags };
              this.lifecycleState.set('ready');
              this.lifecycleCdr.markForCheck();
            }),
            catchError(() => {
              this.lifecycleState.set('unknown');
              this.lifecycleCdr.markForCheck();
              return EMPTY;
            }),
          );
        }),
        takeUntilDestroyed(),
      )
      .subscribe();

    this.sharingTenantId.set(this.filesTenantScopeService?.effectiveTenantId() ?? null);
    this.workspaceIdentityService
      .current()
      .pipe(take(1), takeUntilDestroyed())
      .subscribe((identity) => {
        this.workspaceIdentity.set(identity);
        if (!this.stitchItem?.id && identity) {
          this.sharingTenantId.set(
            this.stitchItem?.accountId ??
              this.suppliedTenantId() ??
              this.filesTenantScopeService?.effectiveTenantId() ??
              identity.memberTenantId,
          );
        }
      });
  }

  private suppliedTenantId(): string | undefined {
    return this.requestContext?.get(OWNING_SPACE_ID) ?? this.requestContext?.get(MAILBOX_TENANT_ID) ?? undefined;
  }

  public override ngOnChanges(changes: SimpleChanges): void {
    if ((changes.file || changes.requestContext) && !this.stitchItem?.id) {
      this.sharingTenantId.set(
        this.stitchItem?.accountId ??
          this.suppliedTenantId() ??
          this.filesTenantScopeService?.effectiveTenantId() ??
          this.workspaceIdentity()?.memberTenantId ??
          null,
      );
    }
    if (changes.file) {
      const request = this.fileIntents.requested();
      // Another file's selection wins over a pending menu intent.
      if (request && !this.fileIntents.matches(request.file, this.file)) this.fileIntents.requested.set(null);
      this.resetReplace();
      this.previewFile = undefined;
    }
    if (changes.file || changes.requestContext) {
      this.lifecycleGeneration++;
      this.lifecycleRebound.next();
      this.lifecycleState.set(changes.file || this.lifecycleState() === 'ready' ? 'ready' : 'unknown');
    }
    super.ngOnChanges(changes);
    if (changes.tab && this.tab === 'comments') this.openItemComments();
    if (changes.tab && this.tab === 'access' && this.insideTeam) this.openWorkspaceAccess();
  }

  /**
   * A person moving a new upload to another workspace. A draft carries only its folder's id, and that folder
   * belongs to the workspace the dialog opened on (the rail checks it), so a different destination drops it and
   * the upload lands at the new workspace's top level (#2290 MJ-01). Re-picking the same workspace keeps it.
   */
  protected pickDestination(tenantId: string): void {
    if (tenantId !== this.sharingTenantId() && !this.stitchItem?.id) {
      this.form.controls.folderId.setValue(null);
    }
    this.sharingTenantId.set(tenantId);
  }

  /** Owned drafts and saved files keep their workspace; unowned drafts follow the picker. */
  protected override tenantOwnerId(): string | undefined {
    return this.stitchItem?.accountId ?? this.sharingTenantId() ?? undefined;
  }

  /** A saved item wins over both the picker and the ambient rail selection. */
  protected override requestConfig(): StitchRequestConfig {
    if (this.stitchItem?.accountId) {
      return { tenantItems: [this.stitchItem] };
    }

    const tenantId = this.sharingTenantId();
    if (!tenantId) {
      return super.requestConfig();
    }
    return { requestContext: new HttpContext().set(OWNING_SPACE_ID, tenantId) };
  }

  download() {
    this.filesService.download(this.stitchItem);
  }

  /** Cross-workspace drops must not bypass Files' honest disabled-action dialog via the base form. */
  private readonly copyDropService = inject(ContainerDndService);
  override dndDrop(dragData: DragData): void {
    if (dragData.type === DragDataTypes.file) {
      const owner = this.stitchItem.accountId;
      const owners = new Set(dragData.data.map((item) => getStringField(item, 'accountId')));
      if (!dragData.data.length || owners.size !== 1 || !owners.has(owner)) {
        this.copyDropService.handleChildItemDrop(
          new Folder({ accountId: owner, title: 'Files' }),
          dragData,
          {
            containerType: DragDataTypes.folder,
            childItemType: DragDataTypes.file,
            childService: this.filesService,
            containerIdField: 'folderId',
            crossTenantCopySupported: true,
            cancelled: this.alive,
            isActive: () => !this.alive.isStopped,
          },
          this.stitchService,
          this.alertService,
        );
        return;
      }
    }
    if (!this.isCrossWorkspaceDrop(dragData)) {
      super.dndDrop(dragData);
      return;
    }

    this.alertService.show({
      title: 'Copy, Move or Stitch?',
      body: 'What do we do with the dropped item?',
      note:
        dragData.type === DragDataTypes.folder
          ? CROSS_WORKSPACE_DROP_TEXT.folderNote
          : dragData.type === DragDataTypes.file
            ? CROSS_WORKSPACE_DROP_TEXT.fileNote
            : CROSS_WORKSPACE_DROP_TEXT.itemNote,
      rightButtons: this.getCrossWorkspaceDropButtons(dragData),
    });
  }

  /** The open file itself, rather than the current rail selection, owns the destination. */
  private isCrossWorkspaceDrop(dragData: DragData): boolean {
    const sourceTenantId = getStringField(dragData.data[0], 'accountId');
    const destinationTenantId = this.stitchItem.accountId;
    return sourceTenantId !== undefined && destinationTenantId !== undefined && sourceTenantId !== destinationTenantId;
  }

  private getCrossWorkspaceDropButtons(dragData: DragData): AlertButtonConfig[] {
    return [
      { title: 'Cancel', close: true },
      {
        title: 'Copy',
        disabled: true,
        tooltip:
          dragData.type === DragDataTypes.folder
            ? CROSS_WORKSPACE_DROP_TEXT.folderCopyTooltip
            : CROSS_WORKSPACE_DROP_TEXT.fileCopyTooltip,
        close: true,
      },
      {
        title: 'Move',
        disabled: true,
        tooltip: CROSS_WORKSPACE_DROP_TEXT.moveTooltip,
        close: true,
      },
      {
        title: 'Stitch',
        disabled: true,
        tooltip: CROSS_WORKSPACE_DROP_TEXT.stitchTooltip,
        close: true,
      },
    ];
  }

  /**
   * Helpers
   */

  get stitchItem(): File {
    return this.file;
  }

  protected override pendingSave(): Observable<void> | undefined {
    return super.pendingSave();
  }

  protected override performCreate(stitchItem: File): Observable<File> {
    const source = this.file.pdfSource;
    if (!source) return super.performCreate(stitchItem);

    return this.filesService.createPDFfromStitch(
      { stitchId: source.id, stitchType: source.itemType, title: stitchItem.title,
        description: stitchItem.description, folderId: stitchItem.folderId },
      { emit: true, toast: true, requestContext: owningTenantContext(source.accountId) },
    ).pipe(
      tap(() => {
        this.form.markAsPristine();
        DirtyStateHelper.clear(this.file);
        this.handleClose();
      }),
      // The queue acknowledgment is not a saved File; the generated file arrives asynchronously.
      switchMap(() => EMPTY),
    );
  }

  protected shouldRefreshList(prev: unknown, current: unknown): boolean {
    return File.shouldRefreshList(prev, current);
  }

  protected fromFormGroup(): File {
    return File.fromFormGroup(this.form);
  }

  protected asFormGroup(): FormGroup<FileFormGroupConfig> {
    return this.file.asFormGroup({ maxFileSize: this.maxUploadSize() });
  }

  protected override cancelInFlightUploads(): void {
    this.filesInput()?.cancelAll();
    super.cancelInFlightUploads();
  }
  protected get insideTeam(): boolean {
    return !!itemWorkspace(this.displayWorkspaceIdentity(), this.file);
  }

  protected get workspaceFooterInputs(): Record<string, unknown> {
    return {
      identity: this.displayWorkspaceIdentity(),
      revision: this.workspaceRevision(),
      item: this.file,
      noun: 'Files',
      collection: this.footerBadge()?.name,
      archived: this.form?.controls.archived.value ?? false,
      deleted: this.form?.controls.deleted.value ?? false,
    };
  }
  protected get workspaceLabelInputs(): Record<string, unknown> {
    return { identity: this.displayWorkspaceIdentity(), revision: this.workspaceRevision(), item: this.file };
  }
  protected readonly openWorkspaceAccess = (): void => {
    this.selectedTab = 'access';
  };

  protected get workspaceAccessInputs(): Record<string, unknown> {
    return { identity: this.displayWorkspaceIdentity(), revision: this.workspaceRevision(), item: this.file };
  }

  protected get workspaceChipInputs(): Record<string, unknown> {
    return { ...this.workspaceAccessInputs, openAccess: this.openWorkspaceAccess, compact: this.contracted };
  }

  protected get itemChangesInputs(): Record<string, unknown> | undefined {
    const item = this.stitchItem;
    if (
      !item?.id ||
      !item.accountId ||
      this.spaceContextStore.type() !== SpaceType.SHARED_SPACE ||
      item.accountId !== this.spaceContextStore.contextId()
    )
      return undefined;
    return { path: '/api/files/files', itemId: item.id, tenantId: item.accountId };
  }

  private readonly workspaceTabControl = viewChild<TabsComponent<string>>('workspaceTabControl');
  protected readonly footerActivityPresentation = computed(
    () => !!this.hostElement.nativeElement.closest('app-full-form-modal') ||
      !this.hostElement.nativeElement.closest('app-dynamic-panel'),
  );
  private readonly footerTarget = computed(() => {
    this.workspaceRevision();
    const item = this.stitchItem;
    const activity = this.itemChangesInputs;
    return JSON.stringify([
      item?.accountId,
      item?.itemType,
      item?.id,
      activity?.['path'],
      activity?.['itemId'],
      activity?.['tenantId'],
    ]);
  });
  protected readonly footerTab = linkedSignal<ItemFooterTab>(() => {
    this.footerTarget();
    return 'comments';
  });
  private readonly showFooterActivity = linkedSignal(() => {
    this.footerTarget();
    return false;
  });
  protected readonly selectFooterTab = (tab: ItemFooterTab): void => {
    this.footerTab.set(tab);
    if (tab === 'comments') this.showFooterActivity.set(false);
  };
  private resyncingWorkspaceTab = false;

  protected selectWorkspaceTab(value: string): void {
    // handleSelect emits the retained main value again; do not clear the footer reveal on that emission.
    if (this.resyncingWorkspaceTab) return;
    const tabs = this.workspaceTabs;
    const target = tabs.find(tab => tab.value === value);
    if (!target || target.disabled) return;
    if (value !== 'activity' || !this.footerActivityPresentation()) {
      this.showFooterActivity.set(false);
      this.selectedTab = value;
      return;
    }
    const retainedIndex = tabs.findIndex(tab => tab.value === this.selectedTab && !tab.disabled);
    const control = this.workspaceTabControl();
    if (!this.itemChangesInputs || retainedIndex < 0 || !control) return;
    this.footerTab.set('activity');
    this.showFooterActivity.set(true);
    this.resyncingWorkspaceTab = true;
    try {
      control.handleSelect(retainedIndex);
    } finally {
      this.resyncingWorkspaceTab = false;
    }
  }

  protected get workspaceTabs(): Tab[] {
    const tabs = this.tabsWithCounts().flatMap((tab) =>
      tab.value === 'comments' && this.itemChangesInputs ? [tab, { title: 'Activity', value: 'activity' }] : [tab],
    );
    const versioned = this.historyAvailable() ? [...tabs, { title: 'Version history', value: 'versions' }] : tabs;
    return this.insideTeam && this.selectedTab === 'access' ? [...versioned, { title: 'Access', value: 'access' }] : versioned;
  }
  protected readonly openItemComments = (): void => {
    this.showFooterActivity.set(false);
    this.selectedTab = 'comments';
  };

  protected get quickCommentInputs(): Record<string, unknown> {
    return {
      identity: this.displayWorkspaceIdentity(),
      revision: this.workspaceRevision(),
      item: this.file,
      noun: 'file',
      selectedTab: this.selectedTab,
      openComments: this.openItemComments,
      activityComponent: this.footerActivityPresentation() ? this.workspaceComponents['app-item-changes'] : undefined,
      activityInputs: this.footerActivityPresentation() ? this.itemChangesInputs : undefined,
      footerTab: this.footerTab(),
      showActivity: this.showFooterActivity(),
      onFooterTabChange: this.selectFooterTab,
    };
  }
}
